import { useToastContext } from '@rtkwlf/fenrir-react';
import {
  EditScannerConfigErrorCode,
  ScannerStatus,
  type EditScannerConfigError,
  type EditScannerConfigInputV2,
  type ResponseError,
} from '@rtkwlf/rendall';
import type { QueryKey, UseMutationOptions } from '@tanstack/react-query';
import { QueryClient, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useOrganizationContext } from '@packages/legacy/organization-context';

import { useRendallApi } from '../use-rendall-api';

const TOAST_DURATION = 20;

export const getEditScannerConfigV2QueryKey = (
  orgId: string,
  scannerId: string,
  scannerStatus?: ScannerStatus,
): QueryKey => [orgId, 'get-scanner-detail', scannerId, scannerStatus];

export const useEditScannerDetailsV2 = (
  scannerId: string,
  scannerStatus?: ScannerStatus,
  options?: UseMutationOptions<
    EditScannerConfigError,
    ResponseError,
    EditScannerConfigInputV2,
    unknown
  >,
) => {
  const api = useRendallApi();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization.id;

  const queryClient = new QueryClient();
  const { t } = useTranslation('telemetryScannerConfig', {
    keyPrefix: 'notifications',
  });
  const { addToast } = useToastContext();

  return useMutation({
    gcTime: 0,
    mutationFn: (scannerConfigV2: EditScannerConfigInputV2) =>
      api.editScannerConfigV2({
        editScannerConfigInputV2: scannerConfigV2,
        organizationId,
        scannerId,
      }),
    onError: async (error) => {
      const editScannerConfigError =
        (await error.response.json()) as EditScannerConfigError;

      let message;
      switch (editScannerConfigError?.errorCode) {
        case undefined:
        case EditScannerConfigErrorCode.UnknownFail:
          message = t('edit.error');
          break;
        case EditScannerConfigErrorCode.ConfigFail:
          message = t('edit.warningIva');
          break;
        case EditScannerConfigErrorCode.PseFail:
          message = t('edit.errorPSE', {
            score: editScannerConfigError.pseScore,
            limit: editScannerConfigError.pseLimit,
          });
          break;
        case EditScannerConfigErrorCode.ConsoleFail:
          message = t('edit.warningConsole');
          break;
        default:
          message = t('edit.error');
      }
      addToast({
        appearance: 'danger',
        duration: TOAST_DURATION,
        message,
        title: t('error'),
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: getEditScannerConfigV2QueryKey(
          organizationId,
          scannerId,
          scannerStatus,
        ),
      });

      // Iva scanner doesn't exist when scanner status is new
      const newScannerIvaFailure =
        data.errorCode === EditScannerConfigErrorCode.ConfigFail &&
        scannerStatus === ScannerStatus.New;
      let message;
      switch (data.errorCode) {
        case EditScannerConfigErrorCode.ConfigFail:
          message = newScannerIvaFailure
            ? t('edit.success')
            : t('edit.warningIva');
          break;
        case EditScannerConfigErrorCode.NameFail:
          message = t('edit.warningCD');
          break;
        case EditScannerConfigErrorCode.PseFail:
          message = t('edit.errorPSE', {
            score: data.pseScore,
            limit: data.pseLimit,
          });
          break;
        case EditScannerConfigErrorCode.ConsoleFail:
          message = t('edit.warningConsole');
          break;
        default:
          message = t('edit.success');
      }
      addToast({
        appearance:
          !data.errorCode || newScannerIvaFailure ? 'success' : 'warning',
        duration: TOAST_DURATION,
        message,
        title:
          !data.errorCode || newScannerIvaFailure ? t('success') : t('warning'),
      });
    },
    ...options,
  });
};
